import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, CheckCircle2,
  Factory, Truck, Flame, Clock, X, History, Eye, Table as TableIcon,
} from 'lucide-react';
import SalesOpsTable from '../components/SalesOpsTable';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Color palette per status — matches the 3 lifecycle dates we track.
const STATUS_STYLE = {
  'Not Accepted':  { bg: 'bg-zinc-800',          text: 'text-zinc-200',    border: 'border-zinc-600',   dot: 'bg-zinc-400',   icon: Clock,        label: 'Pending acceptance' },
  'Accepted':      { bg: 'bg-blue-900/40',       text: 'text-blue-200',    border: 'border-blue-600',   dot: 'bg-blue-400',   icon: CheckCircle2, label: 'Accepted · awaiting production' },
  'In Production': { bg: 'bg-orange-900/40',     text: 'text-orange-200',  border: 'border-orange-600', dot: 'bg-orange-400', icon: Factory,      label: 'On the production floor' },
  'Packing':       { bg: 'bg-amber-900/40',      text: 'text-amber-200',   border: 'border-amber-600',  dot: 'bg-amber-400',  icon: Factory,      label: 'Packing · ready soon' },
  'Dispatched':    { bg: 'bg-green-900/40',      text: 'text-green-200',   border: 'border-green-600',  dot: 'bg-green-400',  icon: Truck,        label: 'Dispatched (history)' },
};

const PRIORITY_STYLE = {
  high:    'bg-red-900/40 text-red-200 border-red-700',
  general: 'bg-zinc-800 text-zinc-400 border-zinc-700',
  low:     'bg-zinc-800/60 text-zinc-500 border-zinc-800',
};

function formatDueBadge(iso, daysToDue) {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  let suffix;
  if (daysToDue === null || daysToDue === undefined) suffix = '';
  else if (daysToDue < 0) suffix = `(${Math.abs(daysToDue)} day${Math.abs(daysToDue) === 1 ? '' : 's'} overdue)`;
  else if (daysToDue === 0) suffix = '(today)';
  else if (daysToDue === 1) suffix = '(tomorrow)';
  else suffix = `(in ${daysToDue} days)`;
  return `${dd}/${mm}/${yy} ${suffix}`;
}

// Lifecycle strip — the 3 tracked dates with their color dots
function LifecycleStrip({ order }) {
  const lanes = [
    { key: 'accepted_at',    label: 'Accepted',       color: 'bg-blue-500',   done: !!order.accepted_at },
    { key: 'in_production_at', label: 'In Production', color: 'bg-orange-500', done: !!order.in_production_at },
    { key: 'dispatched_at',  label: 'Dispatched',     color: 'bg-green-500',  done: !!order.dispatched_at },
  ];
  return (
    <div className="flex items-center gap-1.5 mt-1">
      {lanes.map((l, i) => (
        <React.Fragment key={l.key}>
          <div className={`w-2 h-2 rounded-full ${l.done ? l.color : 'bg-zinc-700'}`} title={`${l.label}: ${order[l.key] ? new Date(order[l.key]).toLocaleDateString('en-IN') : 'pending'}`} />
          {i < lanes.length - 1 && <div className={`w-3 h-[1px] ${lanes[i + 1].done ? l.color : 'bg-zinc-700'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function OrderChip({ order, onClick, compact = false, onDragStart, isDragging }) {
  const style = STATUS_STYLE[order.status] || STATUS_STYLE['Not Accepted'];
  const Icon = style.icon;
  return (
    <div
      role="button"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', order.id);
        onDragStart?.(order);
      }}
      onClick={(e) => { e.stopPropagation(); onClick?.(order); }}
      data-testid={`calendar-order-${order.id}`}
      className={`group w-full text-left rounded-md px-1.5 py-1 border ${style.bg} ${style.border} hover:brightness-125 cursor-grab active:cursor-grabbing transition-all ${isDragging ? 'opacity-40 scale-95' : ''}`}
      title={`${order.client} · ${order.status} · Due ${formatDueBadge(order.primary_due_date, order.days_to_due)} · Drag to reschedule`}
    >
      <div className="flex items-center gap-1 min-w-0">
        <Icon size={10} className={`${style.text} shrink-0`} />
        <span className={`text-[10px] font-semibold ${style.text} truncate`}>{order.client}</span>
      </div>
      {!compact && (
        <>
          <div className="text-[9px] text-zinc-400 truncate font-mono">
            {order.product_code ? `${order.product_code} · ` : ''}{order.product_name}
          </div>
          <LifecycleStrip order={order} />
        </>
      )}
    </div>
  );
}

export default function SalesCalendar() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [data, setData] = useState({ by_date: {}, active: [], history: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('calendar');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  // Drag-and-drop reschedule state
  const [draggingOrder, setDraggingOrder] = useState(null);
  const [dropTargetIso, setDropTargetIso] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const res = await axios.get(`${API}/sales/calendar/view?month=${month}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load calendar');
    } finally { setLoading(false); }
  }, [currentDate]);

  useEffect(() => { load(); }, [load]);

  // Esc key cancels an in-progress drag
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && draggingOrder) {
        setDraggingOrder(null);
        setDropTargetIso(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [draggingOrder]);

  // Build the month grid: 42 cells = 6 weeks
  const cells = useMemo(() => {
    const y = currentDate.getFullYear(), m = currentDate.getMonth();
    const first = new Date(y, m, 1);
    const lead = first.getDay();
    const dim = new Date(y, m + 1, 0).getDate();
    const out = [];
    for (let i = 0; i < lead; i++) out.push({ empty: true, key: `e-${i}` });
    for (let d = 1; d <= dim; d++) {
      const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const orders = (data.by_date[iso] || []).filter(o => statusFilter === 'all' || o.status === statusFilter);
      const isToday = (y === today.getFullYear() && m === today.getMonth() && d === today.getDate());
      out.push({ key: iso, day: d, iso, orders, isToday });
    }
    while (out.length < 42) out.push({ empty: true, key: `t-${out.length}` });
    return out;
  }, [currentDate, data.by_date, statusFilter]);

  const updateStatus = async (order, newStatus) => {
    try {
      await axios.patch(`${API}/sales/orders/${order.id}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Marked ${order.client || 'order'} as ${newStatus}`);
      setSelectedOrder(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  // Drag-and-drop reschedule: move an order chip from its current due date
  // to a new day cell. Updates `dispatch_due_date` (the primary lane that
  // the calendar uses to bucket orders) — Production tasks pull from
  // `production_due_date` separately so this won't disturb the floor plan.
  const rescheduleOrder = async (order, newIso) => {
    if (!order || !newIso) return;
    const fromIso = (order.dispatch_due_date || order.primary_due_date || '').slice(0, 10);
    if (fromIso === newIso) { setDropTargetIso(null); return; }
    setRescheduling(true);
    // Optimistic: move chip locally first
    setData(prev => {
      const next = { ...prev, by_date: { ...prev.by_date } };
      if (fromIso && next.by_date[fromIso]) {
        next.by_date[fromIso] = next.by_date[fromIso].filter(o => o.id !== order.id);
      }
      const moved = { ...order, dispatch_due_date: newIso, primary_due_date: newIso };
      next.by_date[newIso] = [...(next.by_date[newIso] || []), moved];
      return next;
    });
    try {
      await axios.put(
        `${API}/sales/orders/${order.id}`,
        { dispatch_due_date: newIso },
        { withCredentials: true },
      );
      const niceDate = new Date(newIso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      toast.success(`${order.client || 'Order'} rescheduled to ${niceDate}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reschedule failed — reverting');
      load();  // server roll-back
    } finally {
      setRescheduling(false);
      setDraggingOrder(null);
      setDropTargetIso(null);
    }
  };

  const s = data.summary || { active_count: 0, dispatched_count: 0, by_status: {} };

  return (
    <div className="space-y-4" data-testid="sales-calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <CalendarIcon className="text-amber-400" size={22} /> Sales Calendar
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Live pipeline — <span className="text-blue-300">{s.by_status?.['Accepted'] || 0} accepted</span> · <span className="text-orange-300">{s.by_status?.['In Production'] || 0} in production</span> · <span className="text-amber-300">{s.by_status?.['Packing'] || 0} packing</span> · <span className="text-green-300">{s.dispatched_count} dispatched</span></p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTab('calendar')}
            data-testid="tab-calendar"
            className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${tab === 'calendar' ? 'bg-amber-500 text-black border-amber-500 font-semibold' : 'bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700'}`}>
            <CalendarIcon size={12} className="inline mr-1" /> Calendar
          </button>
          <button onClick={() => setTab('ops')}
            data-testid="tab-ops-table"
            className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${tab === 'ops' ? 'bg-orange-500 text-black border-orange-500 font-semibold' : 'bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700'}`}
            title="Day-by-day production & dispatch operations table">
            <TableIcon size={12} className="inline mr-1" /> Ops Table
          </button>
          <button onClick={() => setTab('history')}
            data-testid="tab-history"
            className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${tab === 'history' ? 'bg-green-600 text-white border-green-600 font-semibold' : 'bg-zinc-900 text-zinc-300 border-zinc-800 hover:border-zinc-700'}`}>
            <History size={12} className="inline mr-1" /> History · {s.dispatched_count}
          </button>
        </div>
      </div>

      {/* Legend */}
      {tab !== 'ops' && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl px-4 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap text-[11px]">
            {Object.entries(STATUS_STYLE).filter(([k]) => k !== 'Dispatched' || tab === 'history').map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${v.dot}`} />
                <span className="text-zinc-300">{k}</span>
              </div>
            ))}
          </div>
          {tab === 'calendar' && (
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="status-filter"
              className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1 text-zinc-200 text-xs">
              <option value="all">All active</option>
              <option value="Not Accepted">Not Accepted</option>
              <option value="Accepted">Accepted</option>
              <option value="In Production">In Production</option>
              <option value="Packing">Packing</option>
            </select>
          )}
        </div>
      )}

      {tab === 'ops' ? (
        <SalesOpsTable />
      ) : tab === 'calendar' ? (
        <>
          {/* Month nav */}
          <div className="flex items-center justify-between">
            <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
              data-testid="prev-month" className="text-zinc-400 hover:text-amber-400 p-2 rounded-lg">
              <ChevronLeft size={18} />
            </button>
            <div className="text-zinc-50 font-heading text-lg font-semibold" data-testid="current-month">
              {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>
            <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
              data-testid="next-month" className="text-zinc-400 hover:text-amber-400 p-2 rounded-lg">
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day grid */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden"
            onDragEnd={() => { setDraggingOrder(null); setDropTargetIso(null); }}>
            {draggingOrder && (
              <div className="px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-[11px] flex items-center gap-2" data-testid="drag-hint">
                <span className="font-semibold">Rescheduling</span>
                <span className="text-amber-100/80 truncate">{draggingOrder.client} · {draggingOrder.product_name}</span>
                <span className="ml-auto text-amber-300/80">Drop on a day to set new dispatch date · Esc to cancel</span>
              </div>
            )}
            {rescheduling && (
              <div className="px-3 py-1.5 bg-blue-500/10 border-b border-blue-500/30 text-blue-200 text-[11px]">Saving new dispatch date…</div>
            )}
            <div className="grid grid-cols-7 border-b border-zinc-800 bg-zinc-900/60">
              {DAY_NAMES.map(d => (
                <div key={d} className="text-center text-zinc-500 text-xs font-semibold py-2">{d}</div>
              ))}
            </div>
            {loading ? (
              <div className="p-8 text-center text-zinc-500 text-sm">Loading pipeline…</div>
            ) : (
              <div className="grid grid-cols-7 auto-rows-fr">
                {cells.map(c => {
                  const isDropTarget = !c.empty && dropTargetIso === c.iso && draggingOrder;
                  return (
                  <div key={c.key}
                    onDragOver={c.empty ? undefined : (e) => {
                      if (!draggingOrder) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dropTargetIso !== c.iso) setDropTargetIso(c.iso);
                    }}
                    onDragLeave={c.empty ? undefined : () => {
                      if (dropTargetIso === c.iso) setDropTargetIso(null);
                    }}
                    onDrop={c.empty ? undefined : (e) => {
                      e.preventDefault();
                      const orderId = e.dataTransfer.getData('text/plain');
                      const orderObj = draggingOrder && draggingOrder.id === orderId ? draggingOrder : null;
                      if (orderObj) rescheduleOrder(orderObj, c.iso);
                    }}
                    data-testid={c.empty ? undefined : `calendar-cell-${c.iso}`}
                    className={`border-r border-b border-zinc-800 min-h-[120px] p-1.5 transition-colors ${c.empty ? 'bg-zinc-950/50' : 'bg-zinc-900'} ${c.isToday ? 'bg-amber-500/5 ring-1 ring-amber-500/30' : ''} ${isDropTarget ? 'bg-amber-500/15 ring-2 ring-amber-400/60' : ''}`}>
                    {!c.empty && (
                      <>
                        <div className={`text-[11px] font-mono font-semibold mb-1 ${c.isToday ? 'text-amber-400' : 'text-zinc-500'}`}>
                          {c.day}
                        </div>
                        <div className="space-y-1">
                          {c.orders.slice(0, 3).map(o => (
                            <OrderChip
                              key={o.id}
                              order={o}
                              onClick={setSelectedOrder}
                              compact
                              onDragStart={setDraggingOrder}
                              isDragging={draggingOrder?.id === o.id}
                            />
                          ))}
                          {c.orders.length > 3 && (
                            <div className="text-[10px] text-zinc-500 pl-1">+{c.orders.length - 3} more</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : (
        // History tab
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid="history-list">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
            <History size={14} className="text-green-400" />
            <h3 className="text-zinc-50 font-heading font-semibold text-sm">Dispatched Orders</h3>
            <span className="text-zinc-500 text-xs ml-auto">{data.history.length} record(s)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-zinc-500 text-xs">
                <tr>
                  <th className="text-left px-4 py-2">Voucher</th>
                  <th className="text-left px-4 py-2">Client</th>
                  <th className="text-left px-4 py-2">Product</th>
                  <th className="text-right px-4 py-2">Qty</th>
                  <th className="text-right px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Accepted</th>
                  <th className="text-left px-4 py-2">In Production</th>
                  <th className="text-left px-4 py-2">Dispatched</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {data.history.map(o => (
                  <tr key={o.id} className="hover:bg-zinc-800/40" data-testid={`history-row-${o.id}`}>
                    <td className="px-4 py-2 font-mono text-zinc-300 text-xs">{o.voucher_no || o.id}</td>
                    <td className="px-4 py-2 text-zinc-200">{o.client}</td>
                    <td className="px-4 py-2 text-zinc-400 text-xs">{o.product_code ? `${o.product_code} · ` : ''}{o.product_name}</td>
                    <td className="px-4 py-2 text-right font-mono text-zinc-400">{o.quantity} {o.unit}</td>
                    <td className="px-4 py-2 text-right font-mono text-amber-300">{fmt(o.total_amount)}</td>
                    <td className="px-4 py-2 text-zinc-400 text-xs">{o.accepted_at ? new Date(o.accepted_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="px-4 py-2 text-zinc-400 text-xs">{o.in_production_at ? new Date(o.in_production_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="px-4 py-2 text-green-300 text-xs font-semibold">{o.dispatched_at ? new Date(o.dispatched_at).toLocaleDateString('en-IN') : '—'}</td>
                  </tr>
                ))}
                {data.history.length === 0 && <tr><td colSpan={8} className="text-center text-zinc-500 py-8 text-xs">No dispatched orders yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Order detail modal */}
      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} onAdvance={updateStatus} />
      )}
    </div>
  );
}


function OrderDetailModal({ order, onClose, onAdvance }) {
  const style = STATUS_STYLE[order.status] || STATUS_STYLE['Not Accepted'];
  // Next-step suggestion
  const nextStep = {
    'Not Accepted': 'Accepted',
    'Accepted':     'In Production',
    'In Production':'Packing',
    'Packing':      'Dispatched',
  }[order.status];

  const dates = [
    { label: 'Accepted',     when: order.accepted_at,     color: 'bg-blue-500',   icon: CheckCircle2 },
    { label: 'In Production',when: order.in_production_at,color: 'bg-orange-500', icon: Factory },
    { label: 'Dispatched',   when: order.dispatched_at,   color: 'bg-green-500',  icon: Truck },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="order-detail-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className={`p-4 border-b border-zinc-800 flex items-start justify-between ${style.bg}`}>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-zinc-400">{order.voucher_no || order.id}</div>
            <h2 className={`font-heading text-xl font-semibold ${style.text} truncate`}>{order.client}</h2>
            <div className="text-zinc-400 text-xs mt-1 truncate">
              {order.product_code ? `${order.product_code} · ` : ''}{order.product_name} · {order.quantity} {order.unit}
            </div>
          </div>
          <button onClick={onClose} data-testid="close-modal" className="text-zinc-400 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Status + Priority row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-1 rounded-lg border font-mono font-semibold ${style.bg} ${style.text} ${style.border}`}>
              {order.status}
            </span>
            <span className={`text-xs px-2 py-1 rounded-lg border ${PRIORITY_STYLE[order.priority] || PRIORITY_STYLE.general}`}>
              {order.priority === 'high' && <Flame size={10} className="inline mr-1" />}
              {order.priority}
            </span>
            {order.production_line && (
              <span className="text-xs px-2 py-1 rounded-lg border bg-zinc-800 text-zinc-300 border-zinc-700">
                <Factory size={10} className="inline mr-1" />{order.production_line}
              </span>
            )}
          </div>

          {/* Due date banner */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Primary Due</div>
            <div className={`font-heading text-lg font-semibold mt-0.5 ${
              order.days_to_due === null ? 'text-zinc-300'
              : order.days_to_due < 0   ? 'text-red-300'
              : order.days_to_due <= 2  ? 'text-amber-300'
              : 'text-zinc-100'
            }`} data-testid="due-banner">
              {formatDueBadge(order.primary_due_date, order.days_to_due)}
            </div>
          </div>

          {/* 3-date lifecycle */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Lifecycle dates</div>
            <div className="space-y-2">
              {dates.map(d => {
                const Icon = d.icon;
                return (
                  <div key={d.label} className={`flex items-center gap-3 p-2.5 rounded-lg border ${d.when ? 'border-zinc-700 bg-zinc-900' : 'border-zinc-800 bg-zinc-950/50'}`}>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${d.when ? d.color : 'bg-zinc-800'}`}>
                      <Icon size={13} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-semibold ${d.when ? 'text-zinc-100' : 'text-zinc-500'}`}>{d.label}</div>
                      <div className={`text-[11px] font-mono ${d.when ? 'text-zinc-400' : 'text-zinc-600'}`}>
                        {d.when ? new Date(d.when).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'pending'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Due schedule (production + dispatch) */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-wider text-zinc-500">Production Due</div>
              <div className="text-zinc-200 text-xs font-mono mt-0.5">
                {order.production_due_date ? formatDueBadge(order.production_due_date, Math.floor((new Date(order.production_due_date) - new Date()) / (1000 * 60 * 60 * 24))) : '—'}
              </div>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-wider text-zinc-500">Dispatch Due</div>
              <div className="text-zinc-200 text-xs font-mono mt-0.5">
                {order.dispatch_due_date ? formatDueBadge(order.dispatch_due_date, Math.floor((new Date(order.dispatch_due_date) - new Date()) / (1000 * 60 * 60 * 24))) : '—'}
              </div>
            </div>
          </div>

          {order.remark && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5">
              <div className="text-[9px] uppercase tracking-wider text-amber-400 mb-0.5">Remark</div>
              <div className="text-zinc-200 text-xs">{order.remark}</div>
            </div>
          )}

          <div className="text-right text-amber-300 font-mono text-sm">Order total: <span className="font-bold">{fmt(order.total_amount)}</span></div>

          {/* Advance status */}
          {nextStep && (
            <button onClick={() => onAdvance(order, nextStep)}
              data-testid="advance-status-btn"
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm px-4 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2">
              <Eye size={14} /> Move to {nextStep}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
